import { useEffect, useState } from "react"
import api from "../../lib/api"
import { toastError } from "../../lib/toast"
import ModuleLayout from "../../components/admin/ModuleLayout"import { RowActionsMenu } from "../../components/admin/RowActionsMenu"
import DataTable, { type DataTableColumn } from "../../components/ui/table/DataTable"
import { useServerTable } from "../../hooks/useServerTable"
import { EyeIcon } from "../../icons"

type Submission = {
  id: string
  student_name: string
  project_title: string
  course_title: string
  submission_url?: string
  submission_text?: string
  max_score: number
  score?: number
  status: "pending" | "reviewed" | "approved" | "rejected"
  reviewer_feedback?: string
  submitted_at: string
  project?: {
    id: string
    title: string
    instructions: string
    max_score: number
  }
}

function formatAge(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime()
  const hours = Math.floor(diff / 3_600_000)
  if (hours < 1) return "hace menos de 1h"
  if (hours < 24) return `hace ${hours}h`
  return `hace ${Math.floor(hours / 24)}d`
}

function scoreColor(score: number, max: number): string {
  const ratio = max > 0 ? score / max : 0
  if (ratio >= 0.7) return "bg-green-500"
  if (ratio >= 0.4) return "bg-yellow-500"
  return "bg-error-500"
}

export default function SubmissionsManager() {
  const [search, setSearch] = useState("")
  const [courseFilter, setCourseFilter] = useState("")  const [selected, setSelected] = useState<Submission | null>(null)
  const [score, setScore] = useState<number>(0)
  const [feedback, setFeedback] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [showInstructions, setShowInstructions] = useState(true)

  const {
    rows: submissions,
    total,
    payload,
    page,
    setPage,
    pageSize,
    setPageSize,
    sort,
    setSort,
    loading,
    reload,
  } = useServerTable<Submission>({
    endpoint: "/academy/projects/submissions/pending",
    defaultPageSize: 20,
    defaultSort: { column: "submittedAt", dir: "asc" },
    // Búsqueda y curso viajan al servidor: el total y la paginación reflejan lo filtrado.
    params: { search: search.trim() || undefined, courseTitle: courseFilter || undefined },
    extractRows: (data) => data.submissions ?? [],
    extractTotal: (data) => data.pagination?.total ?? 0,
  })

  // Cursos con entregas pendientes (de todas las páginas); los entrega el servidor.
  const courseOptions: string[] = (payload as { courseOptions?: string[] } | undefined)?.courseOptions ?? []
  const coursesWithPending = courseOptions.length

  useEffect(() => {
    if (selected) {
      setScore(selected.score ?? 0)
      setFeedback(selected.reviewer_feedback ?? "")
    }
  }, [selected])

  function openReview(submission: Submission) {
    setSelected(submission)
  }

  function closeReview() {
    setSelected(null)
  }

  const handleGrade = async (status: "approved" | "rejected") => {
    if (!selected) return
    if (score < 0 || score > selected.max_score) {
      toastError(`La puntuación debe estar entre 0 y ${selected.max_score}`)
      return
    }
    setSubmitting(true)
    try {
      await api.put(`/academy/projects/submissions/${selected.id}/review`, {
        status,
        score,
        reviewer_feedback: feedback,
      })
      setSelected(null)
      await reload()
    } catch (err) {
      toastError(err)
    } finally {
      setSubmitting(false)
    }
  }

  const columns: DataTableColumn<Submission>[] = [
    {
      key: "student_name",
      label: "Alumno",
      render: (sub) => (
        <>
          <p className="font-medium text-gray-900 dark:text-white">{sub.student_name}</p>
          <p className="text-xs text-gray-500">{sub.project_title}</p>
        </>
      ),
    },
    {
      key: "course_title",
      label: "Curso",
      render: (sub) => <span className="text-gray-600 dark:text-gray-400">{sub.course_title}</span>,
    },
    {
      key: "max_score",
      label: "Puntuación máx.",
      align: "center",
      render: (sub) => <span className="text-gray-600 dark:text-gray-400">{sub.max_score} pts</span>,
    },
    {
      key: "submittedAt",
      label: "Entregada",
      sortable: true,
      render: (sub) => <span className="text-xs text-gray-500">{formatAge(sub.submitted_at)}</span>,
    },
    {
      key: "actions",
      label: "Acción",
      align: "right",
      render: (sub) => (
        <div className="flex justify-end">
          <RowActionsMenu actions={[{ label: "Revisar entrega", icon: <EyeIcon />, onClick: () => openReview(sub) }]} />
        </div>
      ),
    },
  ]

  return (
    <ModuleLayout
      breadcrumbs={[{ label: "SateLab", to: "/" }, "Docentes", "Entregas"]}
      title="Entregas"
      subtitle="Revisión y calificación de entregas de proyectos pendientes"
      tabs={[
        { label: "Panel", to: "/instructor/hub" },
        { label: "Entregas", to: "/instructor/submissions", active: true },
        { label: "Exámenes IA", to: "/instructor/section-quizzes" },
        { label: "Estudiantes", to: "/instructor/students" },
      ]}
      metrics={[
        {
          label: "Total pendientes",
          value: String(total),
          tone: total > 0 ? "warning" : "good",
          hint: "Esperando calificación de un instructor",
        },
        { label: "En esta página", value: String(submissions.length) },
        { label: "Cursos con entregas", value: String(coursesWithPending) },
        {
          label: "Entrega más antigua",
          value: submissions.length > 0 ? formatAge(submissions[0].submitted_at) : "—",
        },
      ]}
      toolbar={{
        search: {
          value: search,
          onChange: setSearch,
          placeholder: "Buscar por alumno o proyecto...",
        },
        filters: [
          {
            type: "select",
            key: "course",
            label: "Curso",
            value: courseFilter,
            onChange: setCourseFilter,
            options: [
              { label: "Todos los cursos", value: "" },
              ...courseOptions.map((c) => ({ label: c, value: c })),
            ],
          },
        ],
        onRefresh: reload,
        refreshing: loading,      }}
    >
      <DataTable
        columns={columns}
        rows={submissions}
        rowKey={(sub) => sub.id}
        loading={loading}
        emptyMessage={
          search.trim() || courseFilter
            ? "Ninguna entrega en esta página coincide con el filtro."
            : "No hay entregas pendientes."
        }
        sort={sort}
        onSortChange={setSort}
        page={page}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        total={total}
        onPageChange={setPage}
      />

      {/* Right drawer overlay */}
      {selected && <div className="fixed inset-0 z-40" onClick={closeReview} />}
      <div
        className={`fixed inset-y-0 right-0 z-50 w-[480px] bg-white dark:bg-gray-900 shadow-2xl border-l border-gray-200 dark:border-gray-700 transform transition-transform duration-300 overflow-y-auto ${
          selected ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {selected && (
          <div className="p-6 space-y-5">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">{selected.student_name}</h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  {selected.project_title} · {formatAge(selected.submitted_at)}
                </p>
              </div>
              <button
                onClick={closeReview}
                className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-xl leading-none"
              >
                ✕
              </button>
            </div>

            {selected.project?.instructions && (
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <button
                  onClick={() => setShowInstructions((v) => !v)}
                  className="w-full flex items-center justify-between p-4 text-left bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <span className="font-medium text-sm text-gray-900 dark:text-white">
                    Instrucciones del proyecto
                  </span>
                  <span className="text-gray-500 dark:text-gray-400 text-sm">{showInstructions ? "▲" : "▼"}</span>
                </button>
                {showInstructions && (
                  <div className="px-4 pb-4 pt-0 bg-white dark:bg-gray-900">
                    <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                      {selected.project.instructions}
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 space-y-3">
              <h3 className="font-medium text-sm text-gray-900 dark:text-white">Entrega del estudiante</h3>
              {selected.submission_url && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Enlace</p>
                  <a
                    href={selected.submission_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-blue-600 hover:underline break-all"
                  >
                    {selected.submission_url}
                  </a>
                </div>
              )}
              {selected.submission_text && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Descripción</p>
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {selected.submission_text}
                  </p>
                </div>
              )}
              {!selected.submission_url && !selected.submission_text && (
                <p className="text-sm text-gray-500 dark:text-gray-400">Sin contenido adjunto.</p>
              )}
            </div>

            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 space-y-4">
              <h3 className="font-medium text-sm text-gray-900 dark:text-white">Calificación</h3>

              <div>
                <label className="text-xs text-gray-500 mb-1 block">
                  Puntuación (máx. {selected.max_score})
                </label>
                <input
                  type="number"
                  min={0}
                  max={selected.max_score}
                  value={score}
                  onChange={(e) => setScore(Number(e.target.value))}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <div className="mt-2 h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${scoreColor(score, selected.max_score)}`}
                    style={{ width: `${Math.min(100, selected.max_score > 0 ? (score / selected.max_score) * 100 : 0)}%` }}
                  />
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-500 mb-1 block">Retroalimentación</label>
                <textarea
                  rows={4}
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Escribe tu retroalimentación aquí..."
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => handleGrade("approved")}
                  disabled={submitting}
                  className="flex-1 rounded-lg bg-green-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  ✓ Aprobar
                </button>
                <button
                  onClick={() => handleGrade("rejected")}
                  disabled={submitting}
                  className="flex-1 rounded-lg bg-error-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-error-700 disabled:opacity-50 transition-colors"
                >
                  ✗ Rechazar y pedir revisión
                </button>
              </div>
            </div>
          </div>
        )}
      </div>    </ModuleLayout>
  )
}
